import {
  Product,
  PRODUCT_CONFIG_MAP,
  StartEmailSignupRequest,
  StartGithubSignupRequest
} from '@journeyapps-platform/types-accounts';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useRecaptcha } from 'react-recaptcha-hook';
import { useSystem } from '../../hooks/useSystem';
import { getProductBasedOnUrlParam } from '../../utils/products';
import { ParagraphWidget } from '../../widgets/forms/ParagraphWidget';
import { CenterFormWidget } from '../../widgets/layout/CenterFormWidget';
import { ProductContext, ProductContextWidget } from '../../widgets/product/context';
import { EnterEmailPayload, EnterEmailWidget } from './EnterEmailWidget';
import { FormLayoutWidget } from '../../widgets/layout/FormLayoutWidget';
import { ButtonType, ButtonWidget } from '../../widgets/buttons/ButtonWidget';
import { isJourneyError } from '@journeyapps-platform/micro-errors';
import { ENV } from '../../env';
import * as _ from 'lodash';
import { FooterTextWidget } from '../../widgets/forms/FooterTextWidget';
import { styled } from '../../theme';

const img = require('../../../media/journeyapps.svg');

interface SignupErrorState {
  retryable?: boolean;
  message?: string | React.JSX.Element;
}

export enum ReCAPTCHA_VERSION {
  V2 = 'V2',
  V3 = 'V3'
}

// A custom hook that builds on useLocation to parse
// the query string for you.
function useQuery() {
  return React.useMemo(() => new URL(`${window.location}`).searchParams, [window.location.search]);
}

export interface StartBuildingPageProps {
  product?: Product;
}

/**
 * This is where the main site redirects to
 */
export const StartBuildingPage: React.FC<StartBuildingPageProps> = (props) => {
  const [fallback, setFallback] = useState<boolean>(false);
  const [signupError, setSignupError] = useState<SignupErrorState>({});
  const [complete, setComplete] = useState(false);
  const [payload, setPayload] = useState<Partial<StartEmailSignupRequest>>({
    email: ''
  });
  const system = useSystem();
  const [client] = useState(system.accountsPortalClient);
  const searchQuery = useQuery();

  useEffect(() => {
    document.title = `${PRODUCT_CONFIG_MAP[product].product_name}: Start Building Now`;
  }, []);

  let product = props.product || getProductBasedOnUrlParam();

  const recaptchaV3Execute = useRecaptcha({ sitekey: system.context.recaptcha.v3_site_key, hideDefaultBadge: true });

  // recaptcha will update state internally which results in new functions being returned.
  // we need to access the updated version of the function ref below while in the single invocation
  // of handleSubmit which would otherwise have a handle onto the old function.
  // TLDR: useRecaptcha could use a better API
  const recaptchaRef = React.useRef(recaptchaV3Execute);
  recaptchaRef.current = recaptchaV3Execute;

  const setUnknownError = () => {
    setSignupError({
      message: (
        <>
          Something went wrong. Please try again. If the issue persists, please contact{' '}
          <a href="mailto:support@journeyapps.com">support@journeyapps.com</a>
        </>
      ),
      retryable: true
    });
  };

  const handleSubmit = async (
    updatedPayload: Partial<EnterEmailPayload>,
    options: {
      token: string | null;
      reCAPTCHA_version: ReCAPTCHA_VERSION;
    } = { token: null, reCAPTCHA_version: ReCAPTCHA_VERSION.V3 }
  ) => {
    // reCAPTCHA v3
    if (!options.token && options.reCAPTCHA_version == ReCAPTCHA_VERSION.V3) {
      /**
       * Here we check a few times for a token, since on slow connections the recaptcha scripts may not have loaded.
       * On really slow connections this can literally take like 10 seconds
       */
      try {
        options.token = await new Promise<string>(async (resolve, reject) => {
          for (let i = 0; i < 20; i++) {
            try {
              return resolve(await recaptchaRef.current('submit'));
            } catch (e) {
              await new Promise((resolve) => {
                setTimeout(resolve, 2000);
              });
            }
          }
          reject();
        });
      } catch (e) {
        console.log('error', e);
        setUnknownError();
        return;
      }
    }

    const finalPayload = {
      email: updatedPayload.email!,
      recaptcha_token: options.token || '',
      recaptcha_version: options.reCAPTCHA_version,
      origin: searchQuery.get('s')?.trim(),
      product: product
    };
    setPayload({ ...payload, ...finalPayload });

    const data = await setupFreeTrial(finalPayload);
    // If reCAPTCHA v3 fails, fallback to reCAPTCHA v2
    if (data?.success === true) {
      return setComplete(true);
    }
    if (data?.fallback === true) {
      setFallback(true);
    }
  };

  const setupFreeTrial = async (payload: StartEmailSignupRequest) => {
    if (!payload.email) {
      return;
    }
    try {
      const setupFreeTrialResponse = await client.startEmailSignupWorkflow(payload);

      // Success and fallback we just return the data
      if (setupFreeTrialResponse.success || setupFreeTrialResponse.fallback) {
        return setupFreeTrialResponse;
      }

      return null;
    } catch (e) {
      if (isJourneyError(e)) {
        setSignupError({
          message: e.errorData.description,
          retryable: true
        });
        return null;
      }
    }
    setUnknownError();
  };

  if (complete) {
    return (
      <ProductContextWidget product={product}>
        <CenterFormWidget primaryLabel={'Verify your email'} secondaryLabel={`You're almost there`}>
          <ParagraphWidget>{`Please check your email to proceed.`}</ParagraphWidget>
        </CenterFormWidget>
      </ProductContextWidget>
    );
  }

  const GITHUB_BUTTON = 'Sign up with GitHub';

  return (
    <ProductContextWidget product={product}>
      <CenterFormWidget
        primaryLabel="Get started"
        footer={
          <S.Footer>
            <S.FooterImgLink href="https://journeyapps.com" target="_blank">
              <S.FooterImg src={img} />
            </S.FooterImgLink>
            <S.ProductInfo>{PRODUCT_CONFIG_MAP[product].product_name} is a product of JourneyApps</S.ProductInfo>
          </S.Footer>
        }
      >
        <FormLayoutWidget
          sections={[
            {
              key: 'github',
              elements: [
                <ButtonWidget
                  icon={'fa-brands fa-github' as any}
                  type={ButtonType.SECONDARY}
                  action={() => {
                    const url = new URL(`${ENV.ACCOUNTS_PORTAL_URL}/start-signup/github`);
                    _.forEach(
                      {
                        origin: searchQuery.get('s')?.trim(),
                        product: product
                      } as StartGithubSignupRequest,
                      (value, key) => {
                        if (!!value) {
                          url.searchParams.set(key, value);
                        }
                      }
                    );
                    window.open(url.toString(), '_self');
                  }}
                  label={GITHUB_BUTTON}
                />
              ]
            },
            {
              key: 'submit',
              elements: [
                <EnterEmailWidget
                  fallbackCaptcha={fallback}
                  payload={payload}
                  signupError={signupError}
                  onValueChanged={() => {
                    setSignupError({});
                  }}
                  submit={handleSubmit}
                  onReCaptchaV2={(token) => {
                    handleSubmit(payload, { token: token, reCAPTCHA_version: ReCAPTCHA_VERSION.V2 });
                  }}
                />,
                <ProductContext.Consumer>
                  {(product) => {
                    return (
                      <S.StyledFooter>
                        This site is protected by reCAPTCHA and the Google{' '}
                        <a href="https://policies.google.com/privacy" target="_blank">
                          Privacy Policy
                        </a>{' '}
                        and{' '}
                        <a href="https://policies.google.com/terms" target="_blank">
                          Terms of Service
                        </a>{' '}
                        apply.
                        <br />
                        <br />
                        By signing up, I acknowledge that I have read and understood the {product.product_name}{' '}
                        <a href={product.privacy_policy} target="_blank">
                          Privacy Policy.
                        </a>
                      </S.StyledFooter>
                    );
                  }}
                </ProductContext.Consumer>
              ]
            }
          ]}
        />
      </CenterFormWidget>
    </ProductContextWidget>
  );
};

namespace S {
  export const FooterImgLink = styled.a`
    text-decoration: none;
    padding-bottom: 10px;
  `;

  export const StyledFooter = styled(FooterTextWidget)`
    max-width: 300px;
  `;

  export const Footer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    margin-top: 30px;
  `;

  export const FooterImg = styled.img`
    width: 200px;
    filter: grayscale(1) opacity(0.5);
  `;

  export const ProductInfo = styled.div`
    font-size: 13px;
    color: ${(p) => p.theme.text.secondary};
  `;
}
